/* eslint-disable no-restricted-syntax */
import type { Key } from 'react-stately';
import type { LayoutInfo, LayoutSection, Rect } from './types';

/**
 * Given a layout tree and a visible rect, returns the visible nodes
 * including sections and items.
 * @param visibleRect
 * @param keySizeMap
 * @returns
 */

type GetVisibleNodesProps<T extends LayoutInfo> = {
  persistedKeys?: Set<Key>;
  layoutInfo: Map<Key, T>;
  layout: LayoutInfo[];
  overscanFactor?: number;
  visibleRect: Rect;
};

type GetVisibleNodesResult<T extends LayoutInfo> = {
  visibleNodes: T[];
  isFullyMeasured: boolean;
  visibleNodeSet: Map<Key, T>;
};

export function getVisibleNodes<T extends LayoutInfo>(
  props: GetVisibleNodesProps<T>
): GetVisibleNodesResult<T> {
  const {
    layout,
    layoutInfo,
    visibleRect,
    persistedKeys = new Set(),
    overscanFactor = 0,
  } = props;

  const visibleRectWithOverscan = getRectWithOverscan(
    visibleRect,
    overscanFactor
  );

  let isFullyMeasured = true;
  const visibleNodeSet = new Map<Key, T>();

  /**
   * is a key a persisted key of the parent of a
   * persisted key?
   * @returns boolean
   */

  function isPersistedKey(key: Key) {
    if (persistedKeys.has(key)) {
      return true;
    }

    for (let k of persistedKeys) {
      while (k != null) {
        const layout = layoutInfo.get(k);
        if (!layout) {
          break;
        }

        // @ts-expect-error FIXME(strictNullChecks)
        k = layout.parentKey;
        if (k === key) {
          return true;
        }
      }
    }

    return false;
  }

  function addNodes(nodes: T[], result: T[] = []) {
    // binary search if we can??
    const startIndex = 0;

    for (let i = startIndex; i < nodes.length; i++) {
      const node = nodes[i];

      if (
        isVisible(node, visibleRectWithOverscan) ||
        isPersistedKey(node.key)
      ) {
        if (node.type === 'section') {
          // consider ways that we can avoid creating a new
          // section object here each render, which currently
          // breaks memoization of the section component
          const section = {
            ...node,
            isStickyHeaderPinned: isHeaderPinned(node, visibleRect),
            children: [] as T[],
          } as const;
          result.push(section);
          visibleNodeSet.set(node.key, section);
          addNodes(node.children as unknown as T[], section.children as T[]);
        } else if (node.type === 'item') {
          if (!node.isMeasured) {
            isFullyMeasured = false;
          }
          visibleNodeSet.set(node.key, node);
          result.push(node);
        }
      }
    }

    return result;
  }

  return {
    visibleNodes: addNodes(layout as T[]),
    isFullyMeasured,
    visibleNodeSet,
  };
}

function isHeaderPinned(layout: LayoutSection, visibleRect: Rect) {
  return (
    layout.item.isSticky &&
    layout.item.isVisible &&
    layout.rect.y < visibleRect.y
  );
}

/**
 * Alter the visible rect to include overscan.
 */

export function getRectWithOverscan(rect: Rect, overscanFactor = 0) {
  const overscanY = rect.height * overscanFactor;
  const overscanX = rect.width * overscanFactor;

  return {
    y: rect.y - overscanY,
    height: rect.height + 2 * overscanY,
    x: rect.x - overscanX,
    width: rect.width + 2 * overscanX,
  };
}

export function intersects(rect1: Rect, rect2: Rect) {
  return (
    rect1.x <= rect2.x + rect2.width &&
    rect2.x <= rect1.x + rect1.width &&
    rect1.y <= rect2.y + rect2.height &&
    rect2.y <= rect1.y + rect1.height
  );
}

export function isVisible(layout: LayoutInfo, visibleRect: Rect) {
  return (
    intersects(layout.rect, visibleRect) ||
    (layout.type === 'header' && layout.isSticky)
  );
}
